/**
 * AdminAnnouncementsPage — compose and send player announcements.
 *
 * Three actions:
 *   Preview Email   — renders HTML inline in an iframe
 *   Send Test To Me — isTest: true, only admin receives it
 *   Send to All     — isTest: false, after confirmation dialog
 *
 * Live job progress is tracked via Firestore onSnapshot on the job doc.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../services/firebase';
import './AdminAnnouncementsPage.css';

// ── Email preview builder (mirrors buildAnnouncementEmailHtml in Cloud Function) ──

const SITE_URL = 'https://syndicatorshowdown.com.au';

function buildPreviewHtml({ title, body, ctaLabel, ctaUrl }) {
  const ctaBlock = (ctaLabel && ctaUrl)
    ? `<a href="${SITE_URL}${ctaUrl}"
          style="display:inline-block;background:#126DFF;color:#fff;
                 padding:13px 28px;border-radius:8px;text-decoration:none;
                 font-weight:700;font-size:0.95rem;">
         ${ctaLabel}
       </a>`
    : '';
  return `<!DOCTYPE html>
<html><body style="margin:0;padding:0;background:#f3f4f6;">
<div style="font-family:sans-serif;max-width:600px;margin:0 auto;background:#fff;">
  <div style="background:linear-gradient(135deg,#126DFF,#0a4fd4);padding:24px 32px;">
    <h1 style="color:#fff;margin:0;font-size:1.3rem;letter-spacing:0.02em;">
      🐎 Syndicator Showdown
    </h1>
  </div>
  <div style="padding:32px;">
    <h2 style="color:#1a1a2e;margin:0 0 16px;font-size:1.3rem;">${title || '(no title)'}</h2>
    <p style="color:#374151;line-height:1.7;white-space:pre-wrap;margin:0 0 24px;">${body || '(no message)'}</p>
    ${ctaBlock}
  </div>
  <div style="background:#f9fafb;padding:20px 32px;font-size:0.72rem;
              color:#9ca3af;line-height:1.6;border-top:1px solid #e5e7eb;">
    Syndicator Showdown · Draft · Own · Win<br>
    Gambling Help Online: 1800 858 858 &nbsp;·&nbsp; Lifeline: 13 11 14<br>
    <a href="${SITE_URL}/unsubscribe" style="color:#9ca3af;">Unsubscribe</a>
    &nbsp;·&nbsp;
    <a href="${SITE_URL}/privacy" style="color:#9ca3af;">Privacy Policy</a>
  </div>
</div>
</body></html>`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function AdminAnnouncementsPage() {
  const navigate = useNavigate();

  const [title,       setTitle]       = useState('');
  const [body,        setBody]        = useState('');
  const [ctaLabel,    setCtaLabel]    = useState('');
  const [ctaUrl,      setCtaUrl]      = useState('');
  const [channel,     setChannel]     = useState(['email', 'notification']);
  const [audience,    setAudience]    = useState('all');
  const [reason,      setReason]      = useState('marketing');
  const [status,      setStatus]      = useState(null);   // { type, msg }
  const [jobStatus,   setJobStatus]   = useState(null);   // live job doc
  const [showPreview, setShowPreview] = useState(false);
  const [sending,     setSending]     = useState(false);

  // Toggle a delivery channel on/off
  function toggleChannel(ch) {
    setChannel(prev =>
      prev.includes(ch) ? prev.filter(c => c !== ch) : [...prev, ch]
    );
  }

  // Subscribe to job document for live progress
  function subscribeToJob(jobId) {
    const unsub = onSnapshot(doc(db, 'broadcast_jobs', jobId), snap => {
      if (!snap.exists()) return;
      const job = snap.data();
      setJobStatus(job);
      if (job.status === 'sent') {
        setStatus({
          type: 'success',
          msg: `✓ Sent · Email: ${job.emailSentCount} · Notification: ${job.notificationSentCount}` +
               (job.failedCount > 0 ? ` · Failed: ${job.failedCount}` : ''),
        });
        setSending(false);
        unsub();
      }
      if (job.status === 'failed') {
        setStatus({ type: 'error', msg: `Failed: ${job.lastError}` });
        setSending(false);
        unsub();
      }
    });
  }

  async function handleTestSend() {
    if (!title.trim() || !body.trim()) return;
    setSending(true);
    setStatus(null);
    setJobStatus(null);
    try {
      const fn  = httpsCallable(functions, 'createAnnouncementJob');
      const res = await fn({ title, body, ctaLabel, ctaUrl, channel, audience: 'all', reason, isTest: true });
      setStatus({ type: 'info', msg: `Test queued — job ${res.data.jobId}` });
      subscribeToJob(res.data.jobId);
    } catch (e) {
      setStatus({ type: 'error', msg: `Failed: ${e.message}` });
      setSending(false);
    }
  }

  async function handleSendAll() {
    if (!title.trim() || !body.trim() || !channel.length) return;
    const reasonLabel = reason === 'service'
      ? 'Service / Operational (sends to all users)'
      : reason === 'marketing'
      ? 'Marketing / Promotional (respects opt-out)'
      : 'Game Alert (respects game alert preference)';
    const confirmed = window.confirm(
      `Send "${title}" to ALL players via ${channel.join(' + ')}?\n\n` +
      `Message type: ${reasonLabel}\n\nThis cannot be undone.`
    );
    if (!confirmed) return;
    setSending(true);
    setStatus(null);
    setJobStatus(null);
    try {
      const fn  = httpsCallable(functions, 'createAnnouncementJob');
      const res = await fn({ title, body, ctaLabel, ctaUrl, channel, audience, reason, isTest: false });
      setStatus({ type: 'info', msg: `Sending… job ${res.data.jobId}` });
      subscribeToJob(res.data.jobId);
    } catch (e) {
      setStatus({ type: 'error', msg: `Failed: ${e.message}` });
      setSending(false);
    }
  }

  const canSubmit = title.trim() && body.trim();

  return (
    <div className="annc-page">
      <div className="annc-header">
        <div>
          <h1 className="annc-title">📢 Player Announcements</h1>
          <p className="annc-desc">
            Send a message to players via email, in-app notification, or both.
            Always send a test to yourself first.
          </p>
        </div>
      </div>

      <div className="annc-card">
        <h2 className="annc-card__heading">Compose</h2>

        {/* Title */}
        <div className="annc-field">
          <label className="annc-label">TITLE *</label>
          <input
            className="annc-input"
            value={title}
            onChange={e => setTitle(e.target.value)}
            placeholder="e.g. New Tournament This Saturday!"
            maxLength={100}
          />
        </div>

        {/* Body */}
        <div className="annc-field">
          <label className="annc-label">MESSAGE *</label>
          <textarea
            className="annc-textarea"
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Your message to players…"
            rows={6}
          />
        </div>

        {/* CTA */}
        <div className="annc-row">
          <div className="annc-field" style={{ flex: 1 }}>
            <label className="annc-label">BUTTON LABEL (optional)</label>
            <input
              className="annc-input"
              value={ctaLabel}
              onChange={e => setCtaLabel(e.target.value)}
              placeholder="e.g. Browse Showdowns"
            />
          </div>
          <div className="annc-field" style={{ flex: 1 }}>
            <label className="annc-label">BUTTON URL (optional)</label>
            <input
              className="annc-input"
              value={ctaUrl}
              onChange={e => setCtaUrl(e.target.value)}
              placeholder="e.g. /showdowns"
            />
          </div>
        </div>

        {/* Delivery channels */}
        <div className="annc-field">
          <label className="annc-label">DELIVERY</label>
          <div className="annc-checkrow">
            {[
              { id: 'email',        label: '📧 Email' },
              { id: 'notification', label: '🔔 In-App Notification' },
            ].map(ch => (
              <label key={ch.id} className="annc-check-label">
                <input
                  type="checkbox"
                  checked={channel.includes(ch.id)}
                  onChange={() => toggleChannel(ch.id)}
                />
                {ch.label}
              </label>
            ))}
          </div>
        </div>

        {/* Message type / compliance classification */}
        <div className="annc-field">
          <label className="annc-label">MESSAGE TYPE</label>
          <select
            className="annc-select"
            value={reason}
            onChange={e => setReason(e.target.value)}
          >
            <option value="service">Service / Operational (sends to all)</option>
            <option value="marketing">Marketing / Promotional (respects opt-out)</option>
            <option value="game_alert">Game Alert (respects game alert preference)</option>
          </select>
          <p className="annc-hint">
            Marketing messages respect player email preferences. Service messages always send.
          </p>
        </div>

        {/* Status message */}
        {status && (
          <div className={`admin-status admin-status--${status.type}`}>
            {status.msg}
          </div>
        )}

        {/* Live job progress */}
        {jobStatus && jobStatus.status === 'sending' && (
          <div className="admin-status admin-status--info">
            Sending… · Email: {jobStatus.emailSentCount} ·
            Notification: {jobStatus.notificationSentCount}
            {jobStatus.failedCount > 0 && ` · Failed: ${jobStatus.failedCount}`}
          </div>
        )}

        {/* Action buttons */}
        <div className="annc-actions">
          <button
            className="admin-btn admin-btn--secondary"
            onClick={() => setShowPreview(p => !p)}
          >
            👁️ {showPreview ? 'Hide Preview' : 'Preview Email'}
          </button>
          <button
            className="admin-btn admin-btn--secondary"
            onClick={handleTestSend}
            disabled={!canSubmit || sending}
          >
            🧪 Send Test To Me
          </button>
          <button
            className="admin-btn admin-btn--primary"
            onClick={handleSendAll}
            disabled={!canSubmit || !channel.length || sending}
          >
            📢 Send to All Players
          </button>
        </div>

        {/* Inline email preview */}
        {showPreview && (
          <div className="annc-preview">
            <p className="annc-preview__label">EMAIL PREVIEW</p>
            <iframe
              srcDoc={buildPreviewHtml({ title, body, ctaLabel, ctaUrl })}
              className="annc-preview__frame"
              title="Email preview"
            />
          </div>
        )}
      </div>

      {/* Recent jobs history hint */}
      <p className="annc-footer-hint">
        Broadcast job history is viewable in the{' '}
        <a
          href="https://console.firebase.google.com/project/syndicator-showdown/firestore/data/broadcast_jobs"
          target="_blank"
          rel="noopener noreferrer"
        >
          Firestore console → broadcast_jobs
        </a>.
      </p>
    </div>
  );
}
